/**
 * AuditSearchPanel — cross-account audit search with chain-verified badge.
 *
 * The badge color is the most important signal here: if the verifier sees
 * even one tampered hash anywhere in the chain, this turns red and the
 * operator should treat it as an incident regardless of what's in the
 * filtered window.
 *
 * Includes a CSV-export button that downloads the currently-filtered rows
 * as a comma-separated file. The export is rendered client-side so we
 * don't need a separate server endpoint.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, api, type AuditSearchRow } from "../lib/api";

const LIMIT_OPTIONS = ["50", "100", "200", "500"];

export function AuditSearchPanel() {
  const [rows, setRows] = useState<AuditSearchRow[] | null>(null);
  const [chainVerified, setChainVerified] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [accountFilter, setAccountFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [limit, setLimit] = useState("200");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      setError(null);
      const resp = await api.auditSearch({
        limit: parseInt(limit, 10),
        account_id: accountFilter.trim() || undefined,
        action: actionFilter.trim() || undefined,
      });
      setRows(resp.rows);
      setChainVerified(resp.chain_verified);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "fetch failed");
    } finally {
      setBusy(false);
    }
  }, [limit, accountFilter, actionFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const csvHref = useMemo(() => {
    if (!rows) return null;
    const header = "seq,ts,account_id,actor,action,resource_id,prev_hash,this_hash";
    const escape = (s: string) => '"' + s.replace(/"/g, '""') + '"';
    const lines = rows.map((r) =>
      [
        r.seq,
        r.ts,
        r.account_id ?? "",
        r.actor,
        r.action,
        r.resource_id ?? "",
        r.prev_hash,
        r.this_hash,
      ]
        .map((v) => escape(String(v)))
        .join(","),
    );
    const csv = [header, ...lines].join("\n");
    return URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  }, [rows]);

  if (error) return <Alert color="red">{error}</Alert>;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Audit log</Title>
        <Group>
          {chainVerified !== null && (
            <Badge color={chainVerified ? "green" : "red"} size="lg">
              {chainVerified ? "Chain verified" : "CHAIN BROKEN"}
            </Badge>
          )}
          {csvHref && (
            <Button
              component="a"
              href={csvHref}
              download="audit.csv"
              variant="light"
            >
              Export CSV
            </Button>
          )}
        </Group>
      </Group>

      <Paper p="md" withBorder>
        <Group>
          <TextInput
            label="Account ID"
            placeholder="hex (with or without hyphens)"
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.currentTarget.value)}
            w={320}
          />
          <TextInput
            label="Action"
            placeholder="e.g. enrollment_signed"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.currentTarget.value)}
            w={240}
          />
          <Select
            label="Limit"
            data={LIMIT_OPTIONS}
            value={limit}
            onChange={(v) => setLimit(v ?? "200")}
            w={120}
          />
          <Button onClick={refresh} loading={busy} mt={24}>
            Search
          </Button>
        </Group>
      </Paper>

      {rows === null ? (
        <Loader size="sm" />
      ) : rows.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed">No matching rows.</Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                <Table.Th>When</Table.Th>
                <Table.Th>Account</Table.Th>
                <Table.Th>Actor</Table.Th>
                <Table.Th>Action</Table.Th>
                <Table.Th>Resource</Table.Th>
                <Table.Th>Hash</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.seq}>
                  <Table.Td>{r.seq}</Table.Td>
                  <Table.Td>{r.ts}</Table.Td>
                  <Table.Td style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {r.account_id ? r.account_id.slice(0, 8) + "…" : "—"}
                  </Table.Td>
                  <Table.Td style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {r.actor}
                  </Table.Td>
                  <Table.Td>{r.action}</Table.Td>
                  <Table.Td style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {r.resource_id ? r.resource_id.slice(0, 8) + "…" : "—"}
                  </Table.Td>
                  <Table.Td
                    style={{ fontFamily: "monospace", fontSize: 11 }}
                    title={r.this_hash}
                  >
                    {r.this_hash.slice(0, 12)}…
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
